import { gql } from '@apollo/client'

// Product Queries
export const GET_PRODUCTS = gql`
  query GetProducts($filters: ProductFilters) {
    products(filters: $filters) {
      id
      name
      kind
      active
      display_in_pos
      display_in_webshop
      description
      image_url
      platforms {
        platform { slug displayName sortOrder active }
        enabled
      }
      variants {
        id
        name
        price_cents
        deposit_cents
        sku
        size
        active
      }
      categories {
        id
        name
        platforms {
          platform { slug displayName sortOrder active }
          enabled
        }
      }
      allergen_info_json { allergens custom_warnings }
      attributes_json { attributes spice_level }
      sold_out
      sold_out_until
      platforms_sold_out
      platforms_sold_out_until
    }
  }
`

// Availability page + the "Sold out" entry buttons: every POS product with its
// sold-out state in both dimensions. Lean on purpose — it polls. Product is
// normalized by id, so these fields and GET_PRODUCTS' share one cache entry.
export const GET_PRODUCT_AVAILABILITY = gql`
  query GetProductAvailability($filters: ProductFilters) {
    products(filters: $filters) {
      id
      name
      kind
      variants { id }
      categories { id name }
      sold_out
      sold_out_until
      platforms_sold_out
      platforms_sold_out_until
    }
  }
`

// Meal deals, for the Availability page's blast-radius subtitle ("in Meal Deal").
export const GET_MEAL_DEALS = gql`
  query GetMealDeals {
    products(filters: { kind: BUNDLE, active: true }) {
      id
      name
    }
  }
`

export const GET_PRODUCT = gql`
  query GetProduct($id: Int!) {
    product(id: $id) {
      id
      name
      kind
      active
      display_in_pos
      display_in_webshop
      description
      image_url
      platforms {
        platform { slug displayName sortOrder active }
        enabled
      }
      variants {
        id
        name
        price_cents
        deposit_cents
        sku
        size
        active
      }
      categories {
        id
        name
        platforms {
          platform { slug displayName sortOrder active }
          enabled
        }
      }
      allergen_info_json { allergens custom_warnings }
      attributes_json { attributes spice_level }
      sold_out
      sold_out_until
      platforms_sold_out
      platforms_sold_out_until
    }
  }
`

export const GET_PRODUCT_STATS = gql`
  query GetProductStats {
    productStats {
      total
      active
      inactive
      by_kind {
        base
        modifier
        bundle
      }
    }
  }
`

// Product Variant Queries
export const GET_PRODUCT_VARIANTS = gql`
  query GetProductVariants($filters: ProductVariantFilters) {
    productVariants(filters: $filters) {
      id
      name
      price_cents
      deposit_cents
      sku
      size
      active
      product {
        id
        name
      }
    }
  }
`

export const GET_PRODUCT_VARIANT = gql`
  query GetProductVariant($id: Int!) {
    productVariant(id: $id) {
      id
      name
      price_cents
      deposit_cents
      sku
      size
      active
      product {
        id
        name
      }
    }
  }
`

/** The distinct sizes in use, sorted — the select behind the variant and ladder forms. */
export const GET_VARIANT_SIZES = gql`
  query GetVariantSizes {
    variantSizes
  }
`

// Category Queries
export const GET_CATEGORIES = gql`
  query GetCategories($filters: CategoryFilters) {
    categories(filters: $filters) {
      id
      name
      display_in_pos
      display_in_webshop
      platforms {
        platform { slug displayName sortOrder active }
        enabled
      }
      parent_id
      parent {
        id
        name
      }
      children {
        id
        name
      }
      products {
        id
        name
      }
    }
  }
`

export const GET_CATEGORY = gql`
  query GetCategory($id: Int!) {
    category(id: $id) {
      id
      name
      display_in_pos
      display_in_webshop
      platforms {
        platform { slug displayName sortOrder active }
        enabled
      }
      parent_id
      parent {
        id
        name
      }
      children {
        id
        name
      }
      products {
        id
        name
        description
        image_url
        active
        kind
        variants {
          id
          name
          price_cents
          deposit_cents
          sku
          active
        }
      }
    }
  }
`

export const GET_CATEGORY_TREE = gql`
  query GetCategoryTree {
    categoryTree {
      id
      name
      display_in_pos
      display_in_webshop
      children {
        id
        name
        display_in_pos
        display_in_webshop
        children {
          id
          name
          display_in_pos
          display_in_webshop
        }
      }
    }
  }
`

// Bundle Queries
export const GET_BUNDLE_GROUPS = gql`
  query GetBundleGroups($bundleProductId: Int!) {
    bundleGroups(bundleProductId: $bundleProductId) {
      id
      name
      required_count
      bundle_product {
        id
        name
      }
      options {
        id
        price_delta_cents
        sort_order
        eligible_variant {
          id
          name
          price_cents
          deposit_cents
          sku
          active
          product {
            id
            name
            sold_out
          }
        }
      }
    }
  }
`

export const GET_BUNDLE_OPTIONS = gql`
  query GetBundleOptions($bundleGroupId: Int!) {
    bundleOptions(bundleGroupId: $bundleGroupId) {
      id
      price_delta_cents
      bundle_group_id
      eligible_variant {
        id
        name
        price_cents
        deposit_cents
        sku
        active
        product {
          id
          name
        }
      }
    }
  }
`

export const GET_BUNDLE_OPTION = gql`
  query GetBundleOption($id: Int!) {
    bundleOption(id: $id) {
      id
      price_delta_cents
      bundle_group_id
      eligible_variant {
        id
        name
        price_cents
        deposit_cents
        sku
        active
        product {
          id
          name
        }
      }
    }
  }
`

export const GET_BUNDLE_GROUPS_BY_VARIANT = gql`
  query GetBundleGroupsForVariant($variantId: Int!) {
    bundleGroupsByEligibleVariant(eligibleVariantId: $variantId) {
      id
      name
      required_count
      bundle_product {
        id
        name
        kind
      }
      options {
        id
        price_delta_cents
        eligible_variant {
          id
          name
          price_cents
          deposit_cents
        }
      }
    }
  }
`

// Option Group Queries
export const GET_OPTION_GROUPS = gql`
  query GetOptionGroups($filters: OptionGroupFilters) {
    optionGroups(filters: $filters) {
      id
      name
      description
      min_select
      max_select
      ask_during_add
      active
      sort_order
    }
  }
`

export const GET_OPTION_GROUP = gql`
  query GetOptionGroup($id: Int!) {
    optionGroup(id: $id) {
      id
      name
      description
      min_select
      max_select
      ask_during_add
      active
      sort_order
    }
  }
`

// Option Item Queries
export const GET_OPTION_ITEMS = gql`
  query GetOptionItems($filters: OptionItemFilters) {
    optionItems(filters: $filters) {
      id
      option_group_id
      label
      product_id
      price_delta_cents
      default_selected
      sort_order
      effect_json
      planning_relevant
    }
  }
`

export const GET_OPTION_ITEM = gql`
  query GetOptionItem($id: Int!) {
    optionItem(id: $id) {
      id
      option_group_id
      label
      product_id
      price_delta_cents
      default_selected
      sort_order
      effect_json
      planning_relevant
      image_url
    }
  }
`

export const GET_OPTION_ITEMS_BY_GROUP = gql`
  query GetOptionItemsByGroup($optionGroupId: Int!) {
    optionItemsByGroup(optionGroupId: $optionGroupId) {
      id
      label
      product_id
      price_delta_cents
      default_selected
      sort_order
      effect_json
      planning_relevant
    }
  }
`

export const GET_OPTION_ITEMS_BY_GROUPS = gql`
  query GetOptionItemsByGroups($optionGroupIds: [Int!]!) {
    optionItemsByGroups(optionGroupIds: $optionGroupIds) {
      optionGroupId
      items {
        id
        label
        product_id
        price_delta_cents
        default_selected
        sort_order
        effect_json
        planning_relevant
      }
    }
  }
`

// Variant Option Group Queries
export const GET_VARIANT_OPTION_GROUPS = gql`
  query GetVariantOptionGroups($filters: VariantOptionGroupFilters) {
    variantOptionGroups(filters: $filters) {
      id
      product_variant_id
      option_group_id
      sort_order
      product_variant {
        id
        name
        price_cents
        deposit_cents
        sku
        active
        product {
          id
          name
        }
      }
      option_group {
        id
        name
        min_select
        max_select
        ask_during_add
        active
        sort_order
      }
    }
  }
`

export const GET_VARIANT_OPTION_GROUPS_BY_VARIANT = gql`
  query GetVariantOptionGroupsByVariant($productVariantId: Int!) {
    variantOptionGroupsByVariant(productVariantId: $productVariantId) {
      id
      option_group_id
      sort_order
      option_group {
        id
        name
        min_select
        max_select
        ask_during_add
        active
        sort_order
      }
    }
  }
`

export const GET_VARIANT_OPTION_GROUPS_BY_GROUP = gql`
  query GetVariantOptionGroupsByGroup($optionGroupId: Int!) {
    variantOptionGroupsByGroup(optionGroupId: $optionGroupId) {
      id
      product_variant_id
      sort_order
      product_variant {
        id
        name
        price_cents
        deposit_cents
        sku
        active
        product {
          id
          name
        }
      }
    }
  }
`

// Wizard-specific queries
// Unused until API exposes posProductWizardData (same as publicProductWizardData but filtered by display_in_pos)
export const GET_PRODUCT_WIZARD_DATA = gql`
  query GetProductWizardData($product_id: Int!) {
    publicProductWizardData(product_id: $product_id) {
      defaultVariantId
      steps {
        id
        type
        data
      }
    }
  }
`

export const GET_PRODUCT_VARIANTS_FOR_WIZARD = gql`
  query GetProductVariants($productId: Int!) {
    productVariants(filters: { product_id: $productId, active: true }) {
      id 
      name 
      price_cents
      deposit_cents
      sort_order
    }
  }
`

export const GET_VARIANT_OPTIONS_FOR_WIZARD = gql`
  query GetVariantOptions($variantId: Int!) {
    variantOptionGroupsByVariant(productVariantId: $variantId) {
      option_group {
        id 
        name 
        min_select 
        max_select 
        sort_order
        option_items {
          id 
          label 
          price_delta_cents 
          default_selected 
          sort_order
          product_id
          sold_out
        }
      }
    }
  }
`

export const GET_ELIGIBLE_BUNDLES_FOR_WIZARD = gql`
  query GetEligibleBundles($variantId: Int!) {
    bundleGroupsByEligibleVariant(eligibleVariantId: $variantId) {
      id 
      name 
      required_count
      bundle_product { 
        id 
        name 
      }
      options {
        eligible_variant { 
          id 
          name 
          price_cents
          deposit_cents
        }
        price_delta_cents
      }
    }
  }
`

export const GET_BUNDLES_FOR_VARIANT = gql`
  query BundlesForVariant($variantId: Int!, $includeGroups: Boolean = true, $includeOptions: Boolean = true, $includeEligibleVariant: Boolean = true) {
    bundlesForEligibleVariant(
      eligibleVariantId: $variantId,
      includeGroups: $includeGroups,
      includeOptions: $includeOptions,
      includeEligibleVariant: $includeEligibleVariant
    ) {
      bundle_product { id name }
      groups {
        id
        name
        required_count
        sort_order
        options @include(if: $includeOptions) {
          id
          price_delta_cents
          sort_order
          eligible_variant @include(if: $includeEligibleVariant) {
            id
            name
            price_cents
            deposit_cents
          }
        }
      }
    }
  }
`

export const GET_BUNDLES_FOR_VARIANT_GROUPED = gql`
  query BundlesForVariantGrouped($variantId: Int!) {
    bundlesForEligibleVariantGrouped(eligibleVariantId: $variantId) {
      bundle_product { id name }
      groups {
        id
        name
        required_count
        sort_order
        products {
          product { id name sold_out }
          options {
            id
            price_delta_cents
            sort_order
            eligible_variant { id name price_cents deposit_cents }
          }
        }
      }
    }
  }
`

